import React from "react";
import { useTranslation } from "react-i18next";
import useLanguage from "../../hooks/useLanguage";

// Each link and the invitation section it scrolls to (section keys as in
// invitation/registry.js).
const SECTION_LINKS = [
  { section: "story", target: "our-story", label: "nav.links.story" },
  { section: "venues", target: "where-and-when", label: "nav.links.whereAndWhen" },
  { section: "schedule", target: "schedule", label: "nav.links.schedule" },
  { section: "moments", target: "gallery", label: "nav.links.gallery" },
  { section: "rsvp", target: "rsvp", label: "nav.links.rsvp" },
];

/**
 * `sections` — the invitation sections drawn on the page; a link shows only
 * when its section is there. Left out (the not-found / not-ready pages), every
 * link shows. `showLanguageSwitch` is off in the editor's preview, whose
 * language the editor sets.
 */
const NavigationBar = ({ sections, showLanguageSwitch = true }) => {
  const { t } = useTranslation();
  const { shortLanguage, setLanguage } = useLanguage();
  const links = sections
    ? SECTION_LINKS.filter((link) => sections.includes(link.section))
    : SECTION_LINKS;

  const handleScroll = (e, targetId) => {
    e.preventDefault();
    e.stopPropagation();
    const element = document.getElementById(targetId);
    const navbar = document.querySelector(".navbar");

    if (element) {
      requestAnimationFrame(() => {
        const navbarHeight = navbar ? navbar.offsetHeight : 160;
        const buffer = 0; // Larger buffer to ensure title visibility
        const elementPosition =
          element.getBoundingClientRect().top + window.scrollY;
        const offsetPosition = elementPosition - navbarHeight - buffer;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      });
    }
  };

  return (
    <div
      data-collapse="small"
      data-animation="default"
      data-duration="600"
      data-easing="ease-out-cubic"
      data-easing2="ease-out"
      data-w-id="399ef015-ab98-dfe1-52e9-6d0c683a93fa"
      role="banner"
      className="navbar w-nav"
    >
      <div className="brand-wrap">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          aria-current="page"
          className="brand w-nav-brand w--current"
        >
          <img
            src="/assets/logo/no-symbol-logo/svg/Black logo - no background.svg"
            alt={t("nav.logoAlt")}
            className="nav-logo"
          />
        </a>
      </div>
      <div className="nav-container">
        <nav role="navigation" className="nav-menu w-nav-menu">
          <div className="side-nav panier"></div>
          <div className="main-nav-wrap">
            {links.map((link) => (
              <a
                key={link.section}
                href="#!"
                onClick={(e) => handleScroll(e, link.target)}
                className="nav-link w-inline-block"
              >
                <div>{t(link.label)}</div>
                <div className="ball"></div>
              </a>
            ))}
          </div>
          <div className="side-nav langue">
            {/* The slot stays when the switch is off: it balances the desktop row. */}
            {showLanguageSwitch && (
              <button
                type="button"
                onClick={() => {
                  setLanguage(shortLanguage === "en" ? "el" : "en");
                  // On mobile, the language button lives inside Webflow's
                  // .w-nav-menu overlay. Webflow only auto-closes the overlay
                  // when an <a href="#..."> inside it is clicked — not for
                  // <button>s — so the overlay backdrop stays stuck.
                  // We close it ourselves by driving Webflow's nav API: tell
                  // every .w-nav on the page to close via the `setting`
                  // event Webflow listens for (chunk file, line 8309).
                  if (
                    typeof window !== "undefined" &&
                    window.jQuery &&
                    document.querySelector(".w-nav [data-nav-menu-open]")
                  ) {
                    window.jQuery(".w-nav").triggerHandler("setting.w-nav", {
                      open: false,
                    });
                  }
                }}
                className="nav-link side w-inline-block"
                aria-label={t("nav.language.toggleAriaLabel")}
                style={{ background: "none", border: "none", cursor: "pointer" }}
              >
                {/* Shows the language to switch TO, as a short code (EN/GR).
                    Globe is mobile-only (revealed via CSS in the mobile menu). */}
                <div className="lang-switch">
                  <span className="lang-globe" aria-hidden="true">
                    🌐
                  </span>
                  {shortLanguage === "en"
                    ? t("nav.language.el")
                    : t("nav.language.en")}
                </div>
              </button>
            )}
          </div>
        </nav>
        <div className="menu-button w-nav-button">
          <div className="line _111"></div>
          <div className="line _222"></div>
          <div className="line _333"></div>
        </div>
      </div>
    </div>
  );
};

export default NavigationBar;
